import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { rejectWithApiError } from '../../utils/api';
import { RootReducerState } from '../../redux/Reducers';

type UserType = {
  id: number;
  firstname: string;
  lastname: string;
  email?: string;
};

interface GroupData {
  id: number;
  name: string;
  members: UserType[];
}

interface CreateGroupPayload {
  name: string;
  members: UserType[];
}

interface UpdateGroupPayload {
  groupId: number;
  name?: string;
  members?: UserType[];
}

interface AddMemberPayload {
  groupId: number;
  userId: number;
  userName: string;
  userEmail: string;
}

interface RemoveMemberPayload {
  groupId: number;
  memberId: number;
}

interface ListGroupResponse {
  items: GroupData[];
  meta?: {
    totalItems: number;
    totalPages: number;
    currentPage: number;
  };
}

interface CreateGroupResponse {
  id: number;
  name: string;
  members: UserType[];
}

interface UpdateGroupResponse {
  id: number;
  name: string;
  members: UserType[];
}

interface DeleteGroupResponse {
  success: boolean;
}

// Get all groups
export const getGroupsList = createAsyncThunk<ListGroupResponse, void, { state: RootReducerState }>(
  'managerGroups/getGroupsList',
  async (_, thunkAPI) => {
    try {
      const response = await axios.get<ListGroupResponse>(`${process.env.REACT_APP_API_URL}/ticket/group`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to fetch groups' });
    }
  }
);

// Get group details
export const getGroupDetails = createAsyncThunk<GroupData, { id: number }, { state: RootReducerState }>(
  'managerGroups/getGroupDetails',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.get<GroupData>(`${process.env.REACT_APP_API_URL}/ticket/group/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to fetch group details' });
    }
  }
);

// Create new group
export const createNewGroup = createAsyncThunk<CreateGroupResponse, CreateGroupPayload, { state: RootReducerState }>(
  'managerGroups/createNewGroup',
  async ({ name, members }, thunkAPI) => {
    try {
      const response = await axios.post<CreateGroupResponse>(`${process.env.REACT_APP_API_URL}/ticket/group`, {
        name,
        members,
      });

      thunkAPI.dispatch(getGroupsList());

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to create new group' });
    }
  }
);

// Update group
export const updateGroupName = createAsyncThunk<UpdateGroupResponse, UpdateGroupPayload, { state: RootReducerState }>(
  'managerGroups/updateGroupName',
  async ({ groupId, name, members }, thunkAPI) => {
    try {
      const updateData: any = {};
      if (name !== undefined) updateData.name = name;
      if (members !== undefined) updateData.members = members;

      await axios.patch<UpdateGroupResponse>(`${process.env.REACT_APP_API_URL}/ticket/group/${groupId}`, updateData);

      const updatedGroupDetails = await axios.get<GroupData>(
        `${process.env.REACT_APP_API_URL}/ticket/group/${groupId}`
      );

      return updatedGroupDetails.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to update group' });
    }
  }
);

// Delete group
export const deleteGroup = createAsyncThunk<DeleteGroupResponse, { id: number }, { state: RootReducerState }>(
  'managerGroups/deleteGroup',
  async ({ id }, thunkAPI) => {
    try {
      const response = await axios.delete<DeleteGroupResponse>(`${process.env.REACT_APP_API_URL}/ticket/group/${id}`);

      thunkAPI.dispatch(getGroupsList());

      return response.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to delete group' });
    }
  }
);
export const addMemberToGroup = createAsyncThunk<UpdateGroupResponse, AddMemberPayload, { state: RootReducerState }>(
  'managerGroups/addMemberToGroup',
  async ({ groupId, userId, userName, userEmail }, thunkAPI) => {
    try {
      const currentGroup = await axios.get<GroupData>(`${process.env.REACT_APP_API_URL}/ticket/group/${groupId}`);

      const newMember: UserType = {
        id: userId,
        firstname: userName.split(' ')[0] || userName,
        lastname: userName.split(' ')[1] || '',
        email: userEmail,
      };

      const updatedMembers = [...(currentGroup.data.members || []), newMember];

      await axios.patch<UpdateGroupResponse>(`${process.env.REACT_APP_API_URL}/ticket/group/${groupId}`, {
        name: currentGroup.data.name,
        members: updatedMembers,
      });

      const updatedGroupDetails = await axios.get<GroupData>(
        `${process.env.REACT_APP_API_URL}/ticket/group/${groupId}`
      );

      return updatedGroupDetails.data;
    } catch (error) {
      return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to add member to group' });
    }
  }
);
export const removeMemberFromGroup = createAsyncThunk<
  UpdateGroupResponse,
  RemoveMemberPayload,
  { state: RootReducerState }
>('managerGroups/removeMemberFromGroup', async ({ groupId, memberId }, thunkAPI) => {
  try {
    const currentGroup = await axios.get<GroupData>(`${process.env.REACT_APP_API_URL}/ticket/group/${groupId}`);

    const updatedMembers = (currentGroup.data.members || []).filter(member => member.id !== memberId);

    await axios.patch<UpdateGroupResponse>(`${process.env.REACT_APP_API_URL}/ticket/group/${groupId}`, {
      name: currentGroup.data.name,
      members: updatedMembers,
    });

    const updatedGroupDetails = await axios.get<GroupData>(`${process.env.REACT_APP_API_URL}/ticket/group/${groupId}`);

    return updatedGroupDetails.data;
  } catch (error) {
    return rejectWithApiError({ thunkAPI, error, fallback: 'Failed to remove member from group' });
  }
});
